import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { createCustomElement } from "@angular/elements";
import { Store } from "@ngrx/store";
import { SelectItem } from "primeng/api";
import { Observable } from "rxjs/internal/Observable";
import { debounceTime, filter, map, tap, throttle, throttleTime, withLatestFrom } from "rxjs/operators";
import { Subscription } from "rxjs/Subscription";
import { User } from "../core/model/user.model";
import { PreviewEditTemplateContextComponent } from "../preview-edit-template/preview-edit-template-context.component";
import { PreviewEditTemplateActions } from "../preview-edit-template/state/preview-edit-template.actions";
import { PreviewEditTemplateSelectors } from "../preview-edit-template/state/preview-edit-template.selectors";
import { PreviewEditTemplateService } from "../preview-edit-template/state/preview-edit-template.service";
import { UserSelectors } from '../user/state/user.selectors';
import { SmsConversionService } from "./sms-convertion-ar2-to-ar3/sms-conversion.service";
import { ContentEditableService } from '../core/content-editable/contenteditable.service';
import { AuthenticationSelectors } from '../auth/state/authentication.selectors';

@Component({
  selector: "messages",
  templateUrl: "./messages.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessagesComponent implements OnChanges, OnInit, OnDestroy {
  @Input() isOnlyPreview;
  @Input() selectedUserId;
  @Input() template;
  @Input() templateId;
  @Input() templateUpdateModel;
  @Input() templateFileRef;
  @Input() previewMediaType;
  @Input() hasValueFromDb;
  @Input() userPermissions;
  @Input() communicationsCategoryId;
  @Input() activeIndex;
  @Input() communicationCategory;
  @Input() disablePasteEvent;
  @Input() triggeredCategory;
  @Output() readonly activeTab = new EventEmitter<any>(); // needed in communication for test messages

  smsParts: number;
  isRegistered = false;
  isModelUpdated = false;
  tabIndex = 0;

  contentResponse$: Observable<any>;
  contextVariables$: Observable<any>;
  templateParameters$: Observable<Array<SelectItem>>;
  selectedUser$: Observable<User>;
  previewEditTemplate$: Observable<any>;
  subscription = new Subscription();
  private _timeout: NodeJS.Timeout;
  private _preventChangesOnInit: boolean;
  constructor(
    private previewEditTemplateService: PreviewEditTemplateService,
    private previewEditTemplateSelectors: PreviewEditTemplateSelectors,
    private previewEditSelector: PreviewEditTemplateSelectors,
    private adminUserSelectors: UserSelectors,
    public injector: Injector,
    private store: Store<any>,
    private smsConversionService: SmsConversionService,
    private contentEditableService: ContentEditableService,
    private authenticationSelectors: AuthenticationSelectors
  ) {
    this.templateParameters$ =
      this.previewEditSelector.selectGetTemplateParameters();
    this.contextVariables$ =
      this.previewEditTemplateSelectors.selectGetContextVariable();
    this.previewEditTemplate$ = this.previewEditSelector.selectGetModel();

    this.contentResponse$ = this.previewEditTemplateSelectors
      .selectGetContent()
      .pipe(
        map((contentResponse) => {
          if (contentResponse !== undefined) {
            const content: any = { ...contentResponse };
            let mailResponse = contentResponse.mailContent;
            if (mailResponse !== undefined) {
              mailResponse = mailResponse
                .replace("/<comment/g", "<template-context")
                .replace("/comment>/g", "template-context>")
                .replace("/<context/g", "<template-context")
                .replace("/<\/context>/g", "</template-context")
              ;
              content.mailContent = mailResponse;
            }

            return content;
          }

          return contentResponse;
        })
      );

    // checking if element is already registered (needed when calling from communication)
    // once we NgFactor communication, we need to move this there
    this.isRegistered =
      document.createElement("template-context").constructor !== HTMLElement;

    if (!this.isRegistered) {
      // Convert `PreviewEditTemplateContextComponents` to a custom element.
      const contextElement = createCustomElement(
        PreviewEditTemplateContextComponent,
        { injector }
      );
      // Register the custom element with the browser.
      customElements.define("template-context", contextElement);
    }

    this.selectedUser$ = this.adminUserSelectors.selectSelectedUser().pipe(
      filter((selectedUser) => selectedUser !== undefined),
      filter((selectedUser) => selectedUser.userId !== 0),
      map((selectedUser) => selectedUser)
    );

    this.subscription.add(
      this.previewEditSelector.selectGetMessagePreventChanges().subscribe(preventChanges =>
        this._preventChangesOnInit = preventChanges)

    );
  }

  preprocessContextVariables(updatedModel, type): void {
    const dbModelWithoutContentEditable = {};
    const isSMS = type === 'sms';
    let smsContainTemplateContext = false;
    let shouldPreprocess = true;
    Object.entries(updatedModel).map((tData) => {
      if (tData[1] !== "" && tData[1] !== null) {
        let value: any = tData[1].toString();
        value = value
          .replaceAll('contenteditable="false">', ">")
          .replaceAll(/(<span)([^])*?(>)/g, "")
          .replaceAll(/<\/span>/g, "")
          .replaceAll(/<div><br><\/div>/g, "");
        dbModelWithoutContentEditable[tData[0]] =
          tData[0].indexOf("voice") !== -1 || tData[0].indexOf("sms") !== -1
            ? value.replace(/&nbsp;/g, " ")
            : value;
        if (tData[0].indexOf("sms") !== -1) {
            smsContainTemplateContext = value.indexOf('template-context') !== -1;
        }
      }
      else {
          dbModelWithoutContentEditable[tData[0]] = tData[1];
      }
    });

    if (
      this.templateId !== undefined &&
      Object.keys(dbModelWithoutContentEditable).length !== 0
    ) {
        // if sms and doesnt contain template context - calculating smstext is not needed
        if (isSMS && !smsContainTemplateContext) {
            shouldPreprocess = false;
        }
        if (shouldPreprocess) {
            this.store.dispatch(
                PreviewEditTemplateActions.preprocessModel({
                    mediaType: "sms",
                    template: dbModelWithoutContentEditable,
                    templateId: this.templateId,
                    selectedUserId: this.selectedUserId,
                })
            );
        }
    }
  }

  processPreviewTemplate(updatedModel): any {
    if (this.templateId !== undefined && this.templateId !== "" &&
        this.templateFileRef !== undefined) {
        if (updatedModel.voiceMessage !== undefined && updatedModel.voiceMessage === '') {
            updatedModel.voiceMessage = 'Insert Text Here'
        }
      this.store.dispatch(
        PreviewEditTemplateActions.processPreviewTemplate({
          templateId: this.templateId,
          userId: this.selectedUserId,
          model: updatedModel,
          templateFilename: this.templateFileRef,
          isConfirm:
            this.template !== undefined ? this.template.isConfirm : false,
        })
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.templateUpdateModel !== undefined ||
      changes.hasValueFromDb !== undefined ||
      changes.templateId !== undefined
    ) {
      // from communication
      if (
        this.templateUpdateModel !== undefined &&
        typeof this.templateUpdateModel === "string"
      ) {
        this.isOnlyPreview = JSON.parse(this.isOnlyPreview);
        this.templateUpdateModel = JSON.parse(this.templateUpdateModel);
      }
      /*
      if (this.templateUpdateModel.updatedModel !== undefined) {
        this.preprocessContextVariables(this.templateUpdateModel.updatedModel);
      }
      */

      if (this.communicationCategory !== undefined && !this._preventChangesOnInit) {
        // for communications
        this.templateId = this.templateUpdateModel.updatedModel.templateId !== undefined ? this.templateUpdateModel.updatedModel.templateId : this.templateId;
        this.processPreviewTemplate(this.templateUpdateModel.updatedModel);
      }
    }

    if (changes.activeIndex !== undefined && this.activeIndex === 1 && !this._preventChangesOnInit) {
      this.processPreviewTemplate(this.templateUpdateModel.updatedModel);
    }

    // Reloading email content on preview when saving source
    if (changes.templateFileRef !== undefined && !changes.templateFileRef.isFirstChange() && !this._preventChangesOnInit) {
          this.processPreviewTemplate(this.templateUpdateModel.updatedModel);
    }

    if (changes.activeIndex !== undefined && this.activeIndex === 1) {
      this.tabIndex = 0;
    }

    if (changes.userPermissions !== undefined) {
      if (
        this.userPermissions !== undefined &&
        this.userPermissions !== "" &&
        typeof this.userPermissions === "string"
      ) {
        this.userPermissions = JSON.parse(this.userPermissions);
      }
    }

    if (changes.communicationsCategoryId !== undefined) {
        const categoryType = this.communicationCategory === undefined ? this.communicationsCategoryId : this.communicationCategory;
        this.authenticationSelectors.selectIsAuthenticatedFromAdmin()
            .subscribe(
                isAuthenticatedFromAdmin => {
                    if (isAuthenticatedFromAdmin) {
                        this.validateTemplateOnBlur('', this.templateUpdateModel.updatedModel, categoryType !== undefined);
                    }
                }
            );
    }
  }

  ngOnInit(): void {
    this.setPreventChangesOnInit(true); //#184710957 hack to fix issue on previewing templates
    this.processPreviewTemplate(this.templateUpdateModel.updatedModel);
    this._timeout = setTimeout(() => this.setPreventChangesOnInit(false), 500); //#184710957 hack to fix issue on previewing templates
    const categoryType = this.communicationCategory === undefined ? this.communicationsCategoryId : this.communicationCategory;
    if (this.selectedUserId !== 0) {
      this.store.dispatch(
        PreviewEditTemplateActions.getTemplateParameters({
          userId: this.selectedUserId,
            categoryType
        })
      );
    }

    // For SMS text calculation in Communication
      this.subscription.add(
          this.previewEditSelector
              .selectTemplateUpdateModel()
              .pipe(
                  filter(templateUpdateModel => templateUpdateModel !== undefined),
                  debounceTime(1000)
              )
              .subscribe(templateUpdateModel => {
                  this.preprocessContextVariables(templateUpdateModel.updatedModel, templateUpdateModel.type);
              }
          )
      );

  }

  selectedTab(event): void {
    this.tabIndex = event.index;
    const tabSelected =
      event.index === 0 ? "email" : event.index === 1 ? "sms" : "voice";
    this.activeTab.emit(tabSelected);

    if(event.index === 1){
      this.smsConversionService.displayButton(true);
    }else {
      this.smsConversionService.displayButton(false);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    clearTimeout(this._timeout);
  }

  private setPreventChangesOnInit(value: boolean): void{
    this.store.dispatch(PreviewEditTemplateActions.setMessagePreventChanges({preventChanges:value }));
  }

  // validating for communication and Preview tab
    validateTemplateOnBlur(modelName, updatedModel, isCommunication): void {
        let response: any = this.contentEditableService.validateTemplateOnBlur(modelName, updatedModel);
        let errorMsg = isCommunication ? 'The Default/Selected template contains values which might not let it parse correctly : ' + response.listOfModels + '. Some of them can be rectified by saving the template again.' +
            'If issue still exists, review the template in Admin Portal to auto correct it' :
            'Template contains values which might not let it parse correctly : ' + response.listOfModels + '. Some of them can be rectified by saving the template again.';

        if (response.listOfModels !== '') {
            this.store.dispatch(PreviewEditTemplateActions.displayDialogOnError({error: errorMsg}));
        }
    }
}
